import { PayloadAction } from '@reduxjs/toolkit';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, Observer, timer } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, mergeMap, take } from 'rxjs/operators';

import { PredefinedMilisecondsTypeEnum } from '../../enums';
import GlobalService from '../../services/Global.service';
import { SSE_EVENT_TYPES } from '../../services/closeout/sse-event-types';
import { SSEManager } from '../../services/closeout/SSEManager.service';
import { LoggerService } from '../../services/logger/Logger.service';
import { NullableString } from '../../util/types';
import { isCallStatusPending } from '../../util/error-handling/StatusByCallHelpers';
import { getAccountCloseoutStatus, resetAccountCloseoutState, sseEventReceived } from '../closeout';
import { CloseoutCall } from '../closeout/types';
import { setIsSwitchingAccount, switchAccount } from '../crm';
import { getCurrentAccount } from '../crm/helpers';
import { RootState } from '../index';

import { AccountEndReason, accountFinished } from './index';
import { getCurrentAccountPhase } from './selectors';

import {
  DesiredResources,
  getDesiredResources,
  isSameDesiredResources,
  needsSocketCycle,
} from './resources';

const logger = LoggerService.getLogger();

/**
 * What is actually connected. The controller compares the desired answer against this and applies
 * the difference - it is the only place in the application that opens or closes these.
 */
type ActualResources = {
  /** The account ids the open trading socket was established with. Empty while it is closed. */
  connectedAccountIds: number[];
  isSocketConnected: boolean;
  /** The account the closeout resources currently belong to, `null` when there are none. */
  closeoutAccount: NullableString;
  closeoutStatusFetchedFor: NullableString;
};

const actual: ActualResources = {
  connectedAccountIds: [],
  isSocketConnected: false,
  closeoutAccount: null,
  closeoutStatusFetchedFor: null,
};

/**
 * How long to wait before opening the trading socket for an account that has just become usable.
 *
 * A workaround, and known to be one. The account service announces the account as usable before the
 * trading service is consistently ready to report on it, so connecting immediately sometimes yields
 * a connection that never sends that account's buying power - with no error to show for it. Waiting
 * lets the other side settle.
 *
 * It applies only to an account that becomes usable *during* the session. On a cold start the
 * account has been usable for some time and there is no race, so the socket opens straight away.
 */
const SOCKET_OPEN_DELAY = PredefinedMilisecondsTypeEnum.threeSeconds;

/** Set once the socket state has been applied, so a cold start is told apart from a later change. */
let hasAppliedOnce = false;
let pendingSocketOpen: ReturnType<typeof setTimeout> | null = null;

const cancelPendingSocketOpen = (): void => {
  if (!pendingSocketOpen) return;

  clearTimeout(pendingSocketOpen);
  pendingSocketOpen = null;
};

/** Exposed for tests - the controller keeps its "what is open" state between actions. */
export const resetResourceControllerState = (): void => {
  cancelPendingSocketOpen();
  hasAppliedOnce = false;
  actual.connectedAccountIds = [];
  actual.isSocketConnected = false;
  actual.closeoutAccount = null;
  actual.closeoutStatusFetchedFor = null;
};

const closeSocket = (): void => {
  cancelPendingSocketOpen();

  if (!actual.isSocketConnected) return;

  GlobalService.tradeService?.disconnect();
  actual.isSocketConnected = false;
  actual.connectedAccountIds = [];
};

const openSocket = (usableAccountIds: number[]): void => {
  cancelPendingSocketOpen();
  GlobalService.tradeService?.connect();
  actual.isSocketConnected = true;
  actual.connectedAccountIds = [ ...usableAccountIds ];
};

const openSocketWhenSettled = (usableAccountIds: number[]): void => {
  if (!hasAppliedOnce) {
    openSocket(usableAccountIds);
    return;
  }

  cancelPendingSocketOpen();
  logger.info('[AccountFlow] Waiting for the trading service to settle before opening the socket');
  pendingSocketOpen = setTimeout(() => {
    pendingSocketOpen = null;
    openSocket(usableAccountIds);
  }, SOCKET_OPEN_DELAY);
};

const applyTradingSocket = ({ tradingSocket, usableAccountIds }: DesiredResources): void => {
  if (!tradingSocket) {
    closeSocket();
    hasAppliedOnce = true;
    return;
  }

  if (!actual.isSocketConnected) {
    openSocketWhenSettled(usableAccountIds);
    hasAppliedOnce = true;
    return;
  }

  if (needsSocketCycle(actual.connectedAccountIds, usableAccountIds)) {
    logger.info('[AccountFlow] A new account became usable - cycling the trading socket');
    closeSocket();
    openSocketWhenSettled(usableAccountIds);
  }

  hasAppliedOnce = true;
};

const applyCloseoutStream = ({ closeoutStream }: DesiredResources, observer: Observer<any>): void => {
  if (actual.closeoutAccount !== closeoutStream) {
    actual.closeoutAccount = closeoutStream;
    observer.next(resetAccountCloseoutState());
  }

  SSEManager.getInstance().applyDesired(closeoutStream);
};

const applyCloseoutStatus = ({ closeoutStatus }: DesiredResources, observer: Observer<any>): void => {
  if (!closeoutStatus) {
    actual.closeoutStatusFetchedFor = null;
    return;
  }

  if (actual.closeoutStatusFetchedFor === closeoutStatus) return;

  actual.closeoutStatusFetchedFor = closeoutStatus;
  observer.next(getAccountCloseoutStatus(closeoutStatus));
};

export const recoverTradingSocket = (state: RootState): void => {
  actual.isSocketConnected = false;
  actual.connectedAccountIds = [];
  applyTradingSocket(getDesiredResources(state));
};

const resourceControllerEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => state$.pipe(
  map(getDesiredResources),
  distinctUntilChanged(isSameDesiredResources),
  mergeMap((desired: DesiredResources) => new Observable((observer: Observer<any>) => {
    applyTradingSocket(desired);
    applyCloseoutStream(desired, observer);
    applyCloseoutStatus(desired, observer);
    observer.complete();
  })),
);

const CLOSEOUT_END_REASONS: Record<string, AccountEndReason> = {
  [SSE_EVENT_TYPES.MAX_LOSS_REACHED]: AccountEndReason.Failed,
  [SSE_EVENT_TYPES.PROFIT_TARGET_REACHED]: AccountEndReason.Passed,
};

const closeoutEndEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(sseEventReceived.type),
  filter(({ payload }: PayloadAction<{ type: string }>) => !!CLOSEOUT_END_REASONS[payload?.type]),
  mergeMap(({ payload }: PayloadAction<{ type: string }>) => new Observable((observer: Observer<any>) => {
    const accountReferenceId = getCurrentAccount(state$.value);

    if (accountReferenceId) {
      observer.next(accountFinished({
        accountReferenceId,
        reason: CLOSEOUT_END_REASONS[payload.type],
        phase: getCurrentAccountPhase(state$.value),
      }));
    }

    observer.complete();
  })),
);

const MIN_SWITCH_SPINNER_TIME = PredefinedMilisecondsTypeEnum.twoSeconds;

const switchCompletedEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(switchAccount.type),
  delay(0),
  mergeMap(() => forkJoin([
    timer(MIN_SWITCH_SPINNER_TIME),
    state$.pipe(
      filter(() => !isCallStatusPending(CloseoutCall.getAccountCloseoutStatus, state$.value.closeout.statusByCall)),
      take(1),
    ),
  ]).pipe(map(() => setIsSwitchingAccount(false)))),
);

export default combineEpics(
  resourceControllerEpic,
  closeoutEndEpic,
  switchCompletedEpic,
);
